import moment from "moment";

import type { PublicTrip } from "@busbud/core-types/dist/lib/poll-service/api";

import type { BookingCarouselData } from "@app/components/bookingcom-carousel/types";
import { getAncillaryPurchasedItems } from "@app/helpers/purchase/get-ancillary-purchased-items";
import { getPurchaseTotalPassengersCount } from "@app/helpers/purchase/get-purchase-passengers-count";
import {
  getOutboundDepartureInternationalTime,
  getReturnDepartureInternationalTime
} from "@app/helpers/udm/get-departure-time";
import { getOutboundLegOriginDestinationSegments } from "@app/helpers/udm/get-leg-origin-destination-segments";
import { isRoundtrip } from "@app/helpers/udm/is-roundtrip";
import type { CurrencyCode } from "@app/types/currency";
import type { NapiCancellationOption } from "@app/types/napi-v2-cancellation-types";
import type {
  CfarRefundProtectionPurchasedAncillaryItem,
  HepstarRefundProtectionPurchasedAncillaryItem,
  NapiV2PurchaseDetails,
  NapiV2PurchasedItem
} from "@app/types/napi-v2-purchase-types";
import {
  isCfarRefundProtectionPurchasedAncillaryItem,
  isHepstarRefundProtectionPurchasedAncillaryItem,
  isProtectGroupRefundProtectionPurchasedAncillaryItem
} from "@app/types/napi-v2-purchase-types";

export function hasRefundProtectionPurchased(
  items: NapiV2PurchasedItem[] | undefined
): boolean {
  if (!items || items.length === 0) {
    return false;
  }

  return (
    getHepstarRefundProtectionCompletedPurchasedItems(items).length > 0 ||
    getProtectGroupRefundProtectionCompletedPurchasedItems(items).length > 0
  );
}

export function getCfarItem(
  items: NapiV2PurchasedItem[]
): CfarRefundProtectionPurchasedAncillaryItem | undefined {
  return getAncillaryPurchasedItems(items)
    .filter(isCfarRefundProtectionPurchasedAncillaryItem)
    .find(({ state }) => state === "COMPLETED");
}

export function hasCfarRefundProtectionPurchased(
  items: NapiV2PurchasedItem[]
): boolean {
  return !!getCfarItem(items);
}

function getProtectGroupRefundProtectionCompletedPurchasedItems(
  items: NapiV2PurchasedItem[]
) {
  return getAncillaryPurchasedItems(items)
    .filter(isProtectGroupRefundProtectionPurchasedAncillaryItem)
    .filter(({ state }) => state === "COMPLETED");
}

export function getHepstarRefundProtectionCompletedPurchasedItems(
  items: NapiV2PurchasedItem[]
): HepstarRefundProtectionPurchasedAncillaryItem[] {
  return getAncillaryPurchasedItems(items)
    .filter(isHepstarRefundProtectionPurchasedAncillaryItem)
    .filter(({ state }) => state === "COMPLETED");
}
/**
 * Since we do not offer leg cancellation but only full trip cancellation, it is safe to add up all the cancellable options.
 * As of 2024-01-03:
 * - All the cancellation options are of the same currency.
 *
 * Cancellation options size:
 * 1 item: oneway with the same source, roundtrip with same source
 * 2 items: roundtrip with different sources or bought with separate tickets, oneway interconnection
 *
 * @param cancellation_options
 */
export function getRefundableAmount(
  cancellation_options: NapiCancellationOption[]
): {
  refundable_amount: number;
  currency_code: CurrencyCode;
} {
  const original_payment_method_cancellation_options =
    cancellation_options.filter(({ credit_types }) =>
      credit_types.includes("original_payment_method")
    );
  const voucher_method_cancellation_options = cancellation_options.filter(
    ({ credit_types }) => credit_types.includes("voucher")
  );
  const busbud_credits_method_cancellation_options =
    cancellation_options.filter(({ credit_types }) =>
      credit_types.includes("busbud_credits")
    );

  if (busbud_credits_method_cancellation_options.length) {
    return sumCancellationOptions(busbud_credits_method_cancellation_options);
  }

  // If there are only voucher refund options, refundable amount is 0 as back end is not sending the amount for now
  if (voucher_method_cancellation_options.length) {
    return {
      currency_code:
        // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        voucher_method_cancellation_options[0].refundable_amount.currency,
      refundable_amount: 0
    };
  }

  return sumCancellationOptions(original_payment_method_cancellation_options);
}

function sumCancellationOptions(options: NapiCancellationOption[]): {
  currency_code: CurrencyCode;
  refundable_amount: number;
} {
  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const currency_code = options[0].refundable_amount.currency;
  const refundable_amount = options.reduce(
    (total, option) => total + option.refundable_amount.amount,
    0
  );

  return { currency_code, refundable_amount };
}

export function getBookingCarouselData(
  purchase: NapiV2PurchaseDetails,
  trip: PublicTrip
): BookingCarouselData {
  const {
    destination_segment: { destination }
  } = getOutboundLegOriginDestinationSegments(trip);
  const destination_city_id = destination?.city?.id;
  const destination_city_name = destination?.city?.name;

  const checkin = moment(
    getOutboundDepartureInternationalTime(trip).timestamp
  ).format("YYYY-MM-DD");
  // if roundtrip, checkout is the return departure date, otherwise next day after checkin
  const checkout = isRoundtrip(trip)
    ? moment(getReturnDepartureInternationalTime(trip).timestamp).format(
        "YYYY-MM-DD"
      )
    : moment(checkin).add(1, "days").format("YYYY-MM-DD");

  const passengers = getPurchaseTotalPassengersCount(purchase);

  return {
    checkin,
    checkout,
    destination_city_id,
    destination_city_name,
    passengers
  };
}
